import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserInfo } from 'angular-oauth2-oidc';
import { AjaxService } from './ajax.service';
import { SessionStorageService } from './session-storage.service';
import { BillInterface } from '../interfaces/bill.interface';
import { BillsTableInputDataInterface } from '../interfaces/bills-table-input-data.interface';
import { ExaminationInterface } from '../interfaces/examination.interface';
import { ExaminationsTableInputDataInterface } from '../interfaces/examinations-table-input-data.interface';
import { ParsedRegistrationDataInterface } from '../interfaces/parsed-registration-data.interface';
import { ParsedPlanDataInterface } from '../interfaces/parsed-plan-data.interface';
import { ParsedUserDataInterface } from '../interfaces/parsed-user-data.interface';
import { PlanDataInterface } from '../interfaces/plan-data.interface';
import { RegisterUserResponseInterface } from '../interfaces/register-user-response.interface';
import { RegistrationDataInterface } from '../interfaces/registration-data.interface';
import { TableRowOptionsInterface } from '../interfaces/table-row-options.interface';
import { TreatmentInterface } from '../interfaces/treatment.interface';
import { TreatmentsTableInputDataInterface } from '../interfaces/treatments-table-input-data.interface';
import { UpdateUserDataRequestBodyInterface } from '../interfaces/update-user-data-request-body.interface';
import { UserDataInterface } from '../interfaces/user-data.interface';
import { TableColumnColorEnum } from '../enums/table-column-color.enum';
import { cpfParser } from '../utils/cpf-parser.util';
import { dateFormatter } from '../utils/date-formatter.util';
import { phoneFromDddAndPhoneNumber } from '../utils/phone-from-ddd-and-phone-number.util';
import { zipCodeParser } from '../utils/zip-code-parser.util';
import { BILLS_TABLE_COLUMNS } from '../constants/bills-table-columns.interface';
import { CHANNEL } from '../constants/channel.constant';
import { ENDPOINTS } from '../constants/endpoints.constant';
import { EXAMINATIONS_TABLE_COLUMNS } from '../constants/examinations-table-columns.constant';
import { TREATMENTS_TABLE_COLUMNS } from '../constants/treatments-table-columns.constant';
import { environment } from 'src/environments/environment';
import { stringToBoolean } from 'src/app/shared/utils/string-to-boolean.util';
import { cardName } from '../utils/card-name.util';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly unsetDebugUserCpfValue = 'none';
  private userInfo: UserInfo;
  private userId: string;
  private debugUserCpf: string;
  private userInfoSubject = new BehaviorSubject<UserInfo>(null);
  public userInfoObservable: Observable<UserInfo>;
  private userRegistrations: Record<string, string> = {};

  constructor(private ajaxService: AjaxService, private sessionStorageService: SessionStorageService) {
    this.debugUserCpf = this.sessionStorageService.getJson('app_debugUserCpf');

    this.userInfoObservable = this.userInfoSubject.asObservable();
  }

  private parseUserData(userData: UserDataInterface) {
    
    return {
      nome: userData.nome,
      cpf: cpfParser(userData.cpf),
      chaveCadastrado: userData.chaveCadastrado,
      rg: userData.rg && userData.rg.toString(),
      sexo: userData.sexo,
      dataNascimento: dateFormatter(userData.dataNascimento),
      nomeMae: userData.nomeMae,
      nomePai: userData.nomePai,
      estadoCivilED: userData.estadoCivilED,
      email: userData.email,
      telefoneCelular: phoneFromDddAndPhoneNumber(userData.dddCelular, userData.telefoneCelular),
      telefoneResidencial: phoneFromDddAndPhoneNumber(userData.dddResidencial, userData.telefoneResidencial),
      indEnviaSms: userData.indEnviaSms,
      cep: zipCodeParser(userData.cep),
      tipoLogradouro: userData.tipoLogradouro,
      nomeLogradouro: userData.nomeLogradouro,
      numero: userData.numero,
      complemento: userData.complemento,
      bairro: userData.bairro,
      cidade: userData.cidade,
      uf: userData.uf
    };
  }

  private getExaminationsFullData(examinations: ExaminationInterface[]): ExaminationInterface[] {
    const fullData = examinations.map((item) => {
      return {
        ...item,
        resultado: 'Resultado'
      };
    });

    return fullData;
  }

  private getBillsFullData(bills: BillInterface[]): BillInterface[] {
    const fullData = bills.map((item) => {
      return {
        ...item,
        acoes: 'Emitir 2ª Via da Fatura'
      };
    });

    return fullData;
  }

  private getTreatmentsTableRowOptions(treatments: TreatmentInterface[]): Record<string, TableRowOptionsInterface>[] {
    const treatmentsTableRowOptions: Record<string, TableRowOptionsInterface>[] = treatments.map(() => {
      return {
        prestador: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.DARK,
          iconName: null,
          link: null
        },
        data: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.DARK,
          iconName: null,
          link: null
        },
        valorFranquia: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.DARK,
          iconName: null,
          link: null
        }
      };
    });

    return treatmentsTableRowOptions;
  }

  private getExaminationsTableRowOptions(
    examinations: ExaminationInterface[]
  ): Record<string, TableRowOptionsInterface>[] {
    const examinationsTableRowOptions: Record<string, TableRowOptionsInterface>[] = examinations.map((item) => {
      return {
        exame: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.DARK,
          iconName: null,
          link: null
        },
        data: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.DARK,
          iconName: null,
          link: null
        },
        valorFranquia: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.DARK,
          iconName: null,
          link: null
        },
        prestador: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.DARK,
          iconName: null,
          link: null
        },
        resultado: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.ORANGE,
          iconName: 'arquivo',
          link: item.link
        }
      };
    });

    return examinationsTableRowOptions;
  }

  private getBillsTableRowOptions(bills: BillInterface[]): Record<string, TableRowOptionsInterface>[] {
    const billsTableRowOptions: Record<string, TableRowOptionsInterface>[] = bills.map((item) => {
      return {
        data: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.DARK,
          iconName: null,
          link: null
        },
        vencimento: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.DARK,
          iconName: null,
          link: null
        },
        valor: {
          bold: false,
          disabled: false,
          color: TableColumnColorEnum.DARK,
          iconName: null,
          link: null
        },
        situacao: {
          bold: true,
          disabled: false,
          color:
            item.situacao === 'Paga'
              ? TableColumnColorEnum.GREY
              : item.situacao === 'Aberta'
              ? TableColumnColorEnum.GREEN
              : TableColumnColorEnum.RED,
          iconName: null,
          link: null
        },
        acoes: {
          bold: false,
          disabled: item.situacao === 'Paga',
          color: item.situacao === 'Paga' ? TableColumnColorEnum.DARK : TableColumnColorEnum.ORANGE,
          iconName: 'arquivo',
          link: item.link
        }
      };
    });

    return billsTableRowOptions;
  }

  public setUserInfo(userInfo: UserInfo) {
    this.userInfo = userInfo;

    if (this.userInfo) {
      if (!stringToBoolean(environment.production)) {
        if (!this.debugUserCpf) {
          const debugUserCpf = prompt('CPF para teste:');

          if (debugUserCpf) {
            this.debugUserCpf = debugUserCpf;

            this.sessionStorageService.setJson('app_debugUserCpf', this.debugUserCpf);

            this.userInfo['cpf'] = this.debugUserCpf;
          } else {
            this.sessionStorageService.setJson('app_debugUserCpf', this.unsetDebugUserCpfValue);
          }
        } else if (this.debugUserCpf !== this.unsetDebugUserCpfValue) {
          this.userInfo['cpf'] = this.debugUserCpf;
        }
      }
    }

    this.userInfoSubject.next(this.userInfo);
  }

  public setUserId(userId: string) {
    this.userId = userId;
  }

  public getUserId() {
    return this.userId;
  }
  
  public getUserDoc() {
    return this.userInfo['cpf'];
  }

  public registerUser(userAccessToken: string) {
    return this.ajaxService.post<RegisterUserResponseInterface>(ENDPOINTS.registerUser.url, {}, false, {
      Authorization_lc: `Bearer ${userAccessToken}`
    });
  }

  public getUserAlerts(userAccessToken: string) {
    return this.ajaxService.get<any>(
      `${ENDPOINTS.userAlerts.url}/${CHANNEL}/${this.userInfo['cpf']}`,
      false,
      {
        Authorization_lc: `Bearer ${userAccessToken}`
      },
      'response'
    );
  }

  public async getUserData(userAccessToken: string): Promise<ParsedUserDataInterface> {
    const response = await this.ajaxService.get<UserDataInterface>(
      `${ENDPOINTS.userData.url}/${this.userInfo['cpf']}`,
      false,
      {
        Authorization_lc: `Bearer ${userAccessToken}`
      }
    );

    return this.parseUserData(response);
  }

  public async getRegistrationData(userAccessToken: string, keyRegistration: number): Promise<ParsedUserDataInterface> {
    const response = await this.ajaxService.get<UserDataInterface>(
      `${ENDPOINTS.registrationData.url}/${keyRegistration}`,
      false,
      {
        Authorization_lc: `Bearer ${userAccessToken}`
      }
    );
 
    return this.parseUserData(response);
  }

  public updateUserData(userAccessToken: string, body: UpdateUserDataRequestBodyInterface) {
    return this.ajaxService.put<UserDataInterface>(ENDPOINTS.userData.url, body, {
      Authorization_lc: `Bearer ${userAccessToken}`
    });
  }

  public async getRegistrationsData(userAccessToken: string): Promise<ParsedRegistrationDataInterface[]> {
    const response = await this.ajaxService.get<RegistrationDataInterface[]>(
      `${ENDPOINTS.registrationsList.url}/${this.userInfo['cpf']}`,
      false,
      {
        Authorization_lc: `Bearer ${userAccessToken}`
      }
    );  

    const parsedResponse = response.map((item) => {
      return {
        ...item,
        matricula: item.matricula.toString()
      };
    });   

    return parsedResponse;
  }
  
  public async getActiveRegistrationsData(userAccessToken: string): Promise<ParsedRegistrationDataInterface[]> {
    const response = await this.ajaxService.get<RegistrationDataInterface[]>(
      `${ENDPOINTS.activeRegistrationsList.url}/${this.userInfo['cpf']}`,
      false,
      {
        Authorization_lc: `Bearer ${userAccessToken}`
      }
    );  

    const parsedResponse = response.map((item) => {
      return {
        ...item,
        matricula: item.matricula.toString()
      };
    });   

    return parsedResponse;
  }

  public async getRegistrationsWithDisplayValuesMapper(
    userAccessToken: string,
    options: { pendingRegistrationsOnly: boolean }
  ): Promise<{ registrations: string[]; registrationsDisplayValuesMapper: Record<string, string>,
               registrationsDisplayValuesMapperCardName: Record<string, string> }> {
    let response = await this.getActiveRegistrationsData(userAccessToken);

    if (options.pendingRegistrationsOnly) {
      response = response.filter((item) => item.indFaltaHabilitar);
    }

    const registrations = response.map((item) => item.matricula);

    const registrationsDisplayValuesMapper: Record<string, string> = {};
    this.userRegistrations = {};

    const registrationsDisplayValuesMapperCardName: Record<string, string> = {};

    response.forEach((item) => {
      const registration = item.matricula.toString();

      this.userRegistrations[registration] = item.nome;
      registrationsDisplayValuesMapper[registration] = `${item.matricula} - ${item.nome}`;
      registrationsDisplayValuesMapperCardName[registration] = cardName(`${item.nome}`);
    });

    return {
      registrations,
      registrationsDisplayValuesMapper,
      registrationsDisplayValuesMapperCardName
    };
  }

  public async getPlanData(userAccessToken: string, registration: string): Promise<ParsedPlanDataInterface> {
    const response = await this.ajaxService.get<PlanDataInterface>(`${ENDPOINTS.planData.url}/${registration}`, false, {
      Authorization_lc: `Bearer ${userAccessToken}`
    });

    return {
      ...response,
      dtHabilitacao: dateFormatter(response.dtHabilitacao),
      dtCancSuspensao: dateFormatter(response.dtCancSuspensao),
      matriculaED: {
        ...response.matriculaED,
        nroMatricula: response.matriculaED.nroMatricula && response.matriculaED.nroMatricula.toString(),
        validade: response.matriculaED.validade ? dateFormatter(response.matriculaED.validade) : 'Indeterminado',
        franquia: response.matriculaED.franquia && response.matriculaED.franquia.toString(),
        categoria: response.matriculaED.categoria && response.matriculaED.categoria.toString(),
        cpf: response.matriculaED.cpf && response.matriculaED.cpf.toString(),
        chaveCadastro: response.matriculaED.chaveCadastro && response.matriculaED.chaveCadastro.toString()
      },
      planoVinculoED: {
        ...response.planoVinculoED,
        dtIngresso: dateFormatter(response.planoVinculoED.dtIngresso)
      },
      planoPacED: {
        vlrPremioPac: response.planoPacED.vlrPremioPac && response.planoPacED.vlrPremioPac.toString()
      },
      planoPamesED: {
        ...response.planoPamesED,
        dtCancelPames: dateFormatter(response.planoPamesED.dtCancelPames),
        vlrPremioPames: response.planoPamesED.vlrPremioPames && response.planoPamesED.vlrPremioPames.toString()
      },
      listaPlanoCartaoSolicED: [
        ...response.listaPlanoCartaoSolicED.map((item) => ({
          nroViaCartao: item.nroViaCartao.toString(),
          situacao: item.situacao,
          dtSolicitacao: dateFormatter(item.dtSolicitacao),
          enderecoEnvio: item.enderecoEnvio,
          descricaoRetorno: item.descricaoRetorno
        }))
      ]
    };
  }

  public async getTreatments(options: { top: boolean }): Promise<TreatmentsTableInputDataInterface> {
    const endpointUrl = options.top ? ENDPOINTS.topTreatments.url : ENDPOINTS.treatments.url;

    // TODO: (Futuramente) Remover mock
    const response = await this.ajaxService.getJSON<TreatmentInterface[]>(endpointUrl);

    const tableRowOptions = this.getTreatmentsTableRowOptions(response);

    return {
      columns: TREATMENTS_TABLE_COLUMNS,
      rowOptionsMapper: tableRowOptions,
      data: response
    };
  }

  public async getExaminations(options: { top: boolean }): Promise<ExaminationsTableInputDataInterface> {
    const endpointUrl = options.top ? ENDPOINTS.topExaminations.url : ENDPOINTS.examinations.url;

    // TODO: (Futuramente) Remover mock
    const response = await this.ajaxService.getJSON<ExaminationInterface[]>(endpointUrl);

    const data = this.getExaminationsFullData(response);

    const tableRowOptions = this.getExaminationsTableRowOptions(response);

    return {
      columns: EXAMINATIONS_TABLE_COLUMNS,
      rowOptionsMapper: tableRowOptions,
      data: data
    };
  }

  public async getBills(options: { top: boolean }): Promise<BillsTableInputDataInterface> {
    const endpointUrl = options.top ? ENDPOINTS.topBills.url : ENDPOINTS.bills.url;

    // TODO: (Futuramente) Remover mock
    const response = await this.ajaxService.getJSON<BillInterface[]>(endpointUrl);

    const data = this.getBillsFullData(response);

    const tableRowOptions = this.getBillsTableRowOptions(response);

    return {
      columns: BILLS_TABLE_COLUMNS,
      rowOptionsMapper: tableRowOptions,
      data: data
    };
  }

  public nameForRegistration(registration: string): string {
    return this.userRegistrations[registration];
  }
  
}
